import { useEffect, useRef, useState } from "react";
import { Icon } from "./icons";
import {
  type AcademicCalendar,
  addDays,
  todayIso,
  weekIndexOf,
  weekday,
} from "../shared/weeks";
import {
  bellFor,
  dayNumber,
  longDate,
  minutes,
  plural,
  weekSpan,
  clockMinutes,
} from "./calendar";

const SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const DAYS = ["понедельник", "вторник", "среда", "четверг", "пятница", "суббота", "воскресенье"];

interface Coverage {
  documents: number;
  total: number;
  failed: string[];
  withoutSemester: string[];
  year: string;
}
interface DayLesson {
  start: string;
  end: string | null;
  subject: string;
  type: string;
  teachers: string[];
  rooms: string[];
  moved: boolean;
  /** "Полоцк", when the document names the town (API 14). */
  place?: string | null;
  distance: boolean;
  groups: string[];
  documents: { id: string; name: string }[];
  condition: string;
  uncertain: boolean;
}
interface FreeResult {
  bell: { pair: number; start: string; end: string };
  free: { letter: string; rooms: string[]; label?: string }[];
  freeCount: number;
  busyCount: number;
  knownRooms: number;
  maybe: { room: string; lessons: { subject: string; groups: string[]; condition: string }[] }[];
  coverage: Coverage;
}
type Target = { kind: "teacher"; name: string; faculties?: string[] } | { kind: "room"; room: string };

async function api<T>(url: string): Promise<T> {
  const r = await fetch(url).catch(() => {
    throw new Error("Сервер недоступен. Поиск по университету работает только онлайн.");
  });
  const body = await r.json();
  if (!r.ok) throw new Error(body.error || "Не удалось выполнить поиск.");
  return body;
}
const RECENT = "psu-recent-search-v1";
function readRecent(): Target[] {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}
function remember(target: Target) {
  const same = (t: Target) =>
    t.kind === target.kind &&
    (t.kind === "teacher" ? t.name === (target as typeof t).name : t.room === (target as typeof t).room);
  try {
    localStorage.setItem(
      RECENT,
      JSON.stringify([target, ...readRecent().filter((t) => !same(t))].slice(0, 6)),
    );
  } catch {
    /* Recent searches are a convenience only. */
  }
}
/** Opens on today; on Sunday on Monday, when lessons resume. */
function startDate() {
  const today = todayIso();
  return weekday(today) === 7 ? addDays(today, 1) : today;
}

function CoverageNote({ coverage, free = false }: { coverage: Coverage; free?: boolean }) {
  return (
    <p className="coverage-note">
      По расписаниям из Word: учтено {coverage.documents} из {coverage.total}{" "}
      {plural(coverage.total, ["документа", "документов", "документов"])} каталога{" "}
      {coverage.year}.
      {coverage.withoutSemester.length > 0 &&
        ` Для ${coverage.withoutSemester.length} ${plural(coverage.withoutSemester.length, ["документа", "документов", "документов"])} семестр не найден в таблице недель — они не учтены.`}
      {coverage.failed.length > 0 &&
        ` Не распознано: ${coverage.failed.length}.`}
      {free &&
        " Аудитория может быть занята мероприятием, консультацией или закрыта — расписание этого не знает."}
    </p>
  );
}

function DayPicker({
  date,
  onChange,
  calendar,
}: {
  date: string;
  onChange: (date: string) => void;
  calendar: AcademicCalendar | null;
}) {
  const today = todayIso();
  const monday = addDays(date, 1 - weekday(date));
  const week = calendar?.weeks[weekIndexOf(calendar, date)];
  return (
    <div className="search-days">
      <div className="week-switcher">
        <button
          className="icon-button"
          aria-label="Предыдущая неделя"
          onClick={() => onChange(addDays(date, -7))}
        >
          <Icon name="chevron_left" />
        </button>
        <div className="week-label" aria-live="polite">
          <strong>{week ? `Неделя ${week.n}` : "Вне таблицы недель"}</strong>
          {week && (
            <span className={`week-chip ${week.color}`}>
              {week.color === "white" ? "белая" : "зелёная"}
            </span>
          )}
          <small>{weekSpan(monday, addDays(monday, 6))}</small>
        </div>
        {(date < addDays(today, 1 - weekday(today)) ||
          date > addDays(today, 7 - weekday(today))) && (
          <button className="today-button" onClick={() => onChange(startDate())}>
            Сегодня
          </button>
        )}
        <button
          className="icon-button"
          aria-label="Следующая неделя"
          onClick={() => onChange(addDays(date, 7))}
        >
          <Icon name="chevron_right" />
        </button>
      </div>
      <div className="day-strip dated" aria-label="Дни недели">
        {SHORT.map((label, i) => {
          const iso = addDays(monday, i);
          return (
            <button
              key={iso}
              className={`${iso === date ? "selected" : ""} ${iso === today ? "today" : ""}`}
              aria-pressed={iso === date}
              aria-label={`${DAYS[i]}, ${longDate(iso)}${iso === today ? ", сегодня" : ""}`}
              onClick={() => onChange(iso)}
            >
              <span>{label}</span>
              <span className="day-number">{dayNumber(iso)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Search({
  calendar,
  online,
  now,
}: {
  calendar: AcademicCalendar | null;
  online: boolean;
  now: number;
}) {
  const [tab, setTab] = useState<"people" | "free">("people"),
    [query, setQuery] = useState(""),
    [results, setResults] = useState<{ teachers: { name: string; count: number; faculties: string[] }[]; rooms: { room: string; count: number }[] } | null>(null),
    [target, setTarget] = useState<Target | null>(null),
    [date, setDate] = useState(startDate),
    [day, setDay] = useState<{ lessons: DayLesson[]; coverage: Coverage } | null>(null),
    [free, setFree] = useState<FreeResult | null>(null),
    [pair, setPair] = useState(() => {
      const bells = calendar?.bells || [];
      const m = clockMinutes(now);
      return (
        bells.find((b) => m < minutes(b.end))?.pair || bells[0]?.pair || 1
      );
    }),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [recent, setRecent] = useState(readRecent),
    [openMaybe, setOpenMaybe] = useState(false);
  const request = useRef(0);

  useEffect(() => {
    if (query.trim().length < 2) return setResults(null);
    const id = ++request.current;
    const timer = window.setTimeout(async () => {
      try {
        const r = await api<NonNullable<typeof results>>(
          `/api/directory/search?q=${encodeURIComponent(query.trim())}`,
        );
        if (id === request.current) {
          setResults(r);
          setError("");
        }
      } catch (e) {
        if (id === request.current) setError((e as Error).message);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!target) return setDay(null);
    const id = ++request.current;
    setLoading(true);
    const param =
      target.kind === "teacher"
        ? `teacher=${encodeURIComponent(target.name)}`
        : `room=${encodeURIComponent(target.room)}`;
    api<{ lessons: DayLesson[]; coverage: Coverage }>(`/api/directory/day?${param}&date=${date}`)
      .then((r) => id === request.current && (setDay(r), setError("")))
      .catch((e) => id === request.current && setError(e.message))
      .finally(() => id === request.current && setLoading(false));
  }, [target, date]);

  useEffect(() => {
    if (tab !== "free") return;
    const id = ++request.current;
    setLoading(true);
    api<FreeResult>(`/api/directory/free?date=${date}&pair=${pair}`)
      .then((r) => id === request.current && (setFree(r), setError("")))
      .catch((e) => id === request.current && setError(e.message))
      .finally(() => id === request.current && setLoading(false));
  }, [tab, date, pair]);

  const open = (t: Target) => {
    remember(t);
    setRecent(readRecent());
    setTarget(t);
  };
  const today = todayIso(new Date(now));
  const clock = clockMinutes(now);

  if (!online)
    return (
      <div className="empty-state">
        <Icon name="cloud_off" size={40} />
        <h3>Нет сети</h3>
        <p>
          Поиск преподавателей и аудиторий идёт по всем расписаниям университета
          на сервере и работает только онлайн. Ваше расписание доступно без сети.
        </p>
      </div>
    );

  return (
    <section className="search-screen" aria-label="Поиск по университету">
      {!target && (
        <div className="view-switch search-tabs" aria-label="Что искать">
          <button
            className={tab === "people" ? "selected" : ""}
            aria-pressed={tab === "people"}
            onClick={() => setTab("people")}
          >
            {tab === "people" && <Icon name="check" size={20} />}
            Кто где
          </button>
          <button
            className={tab === "free" ? "selected" : ""}
            aria-pressed={tab === "free"}
            onClick={() => setTab("free")}
          >
            {tab === "free" && <Icon name="check" size={20} />}
            Свободные аудитории
          </button>
        </div>
      )}
      {error && (
        <div role="status" className="status-message error">
          <Icon name="error" />
          <span>{error}</span>
        </div>
      )}

      {tab === "people" && !target && (
        <>
          <label className="search-bar">
            <Icon name="search" />
            <input
              type="search"
              placeholder="Фамилия или номер аудитории"
              aria-label="Преподаватель или аудитория"
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                className="icon-button"
                aria-label="Очистить поиск"
                onClick={() => setQuery("")}
              >
                <Icon name="close" />
              </button>
            )}
          </label>
          {!results && recent.length > 0 && (
            <div className="result-group">
              <h3>Недавние</h3>
              <div className="result-list">
                {recent.map((t) => (
                  <button
                    key={t.kind === "teacher" ? `t${t.name}` : `r${t.room}`}
                    onClick={() => open(t)}
                  >
                    <span className="menu-icon">
                      <Icon name={t.kind === "teacher" ? "person" : "meeting_room"} />
                    </span>
                    <span>{t.kind === "teacher" ? t.name : `Аудитория ${t.room}`}</span>
                    <Icon name="chevron_right" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {!results && !recent.length && (
            <div className="empty-state">
              <Icon name="person_search" size={40} />
              <h3>Кто где и когда</h3>
              <p>
                Найдите преподавателя или аудиторию — покажем занятия на выбранный
                день по всем расписаниям университета.
              </p>
            </div>
          )}
          {results && (
            <>
              {results.teachers.length > 0 && (
                <div className="result-group">
                  <h3>Преподаватели</h3>
                  <div className="result-list">
                    {results.teachers.map((t) => (
                      <button
                        key={t.name}
                        onClick={() => open({ kind: "teacher", name: t.name, faculties: t.faculties })}
                      >
                        <span className="menu-icon">
                          <Icon name="person" />
                        </span>
                        <span>
                          {t.name}
                          <small>{t.faculties.join(", ") || "Факультет не указан"}</small>
                        </span>
                        <Icon name="chevron_right" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {results.rooms.length > 0 && (
                <div className="result-group">
                  <h3>Аудитории</h3>
                  <div className="room-chips">
                    {results.rooms.map((r) => (
                      <button key={r.room} onClick={() => open({ kind: "room", room: r.room })}>
                        <Icon name="meeting_room" size={18} />
                        {r.room}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!results.teachers.length && !results.rooms.length && (
                <div className="empty-state">
                  <Icon name="search_off" size={40} />
                  <h3>Никого не нашли</h3>
                  <p>
                    Проверьте фамилию или номер. Ищем только тех, кто указан в
                    расписаниях каталога.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === "people" && target && (
        <>
          <div className="target-header">
            <button
              className="icon-button"
              aria-label="Назад к поиску"
              onClick={() => setTarget(null)}
            >
              <Icon name="chevron_left" />
            </button>
            <span className="menu-icon">
              <Icon name={target.kind === "teacher" ? "person" : "meeting_room"} />
            </span>
            <div>
              <h2>{target.kind === "teacher" ? target.name : `Аудитория ${target.room}`}</h2>
              {target.kind === "teacher" && target.faculties?.length ? (
                <small>{target.faculties.join(", ")}</small>
              ) : null}
            </div>
          </div>
          <DayPicker date={date} onChange={setDate} calendar={calendar} />
          <h3 className="search-date">
            {DAYS[weekday(date) - 1][0].toUpperCase() + DAYS[weekday(date) - 1].slice(1)},{" "}
            {longDate(date)}
            {date === today ? " · сегодня" : ""}
          </h3>
          {loading && !day ? (
            <div className="schedule-skeleton" role="status" aria-label="Ищем занятия">
              <span />
              <span />
            </div>
          ) : day && day.lessons.length ? (
            <div className="lesson-list">
              {day.lessons.map((l, i) => {
                const bell = bellFor(calendar, l.start);
                const end = l.end || bell?.end || null;
                const now =
                  date === today && end && minutes(l.start) <= clock && clock < minutes(end);
                return (
                  <section key={i} className={`time-slot ${now ? "now" : ""}`}>
                    <h3 className="slot-time">
                      <time>{l.start}</time>
                      <span>{end ? `— ${end}` : "конец не указан"}</span>
                      {bell && <span className="slot-pair">{bell.pair} пара</span>}
                      {now && <span className="live-chip on">Идёт сейчас</span>}
                    </h3>
                    <div className="slot-lessons">
                      <div className="lesson-card directory-card">
                        <span className="lesson-body">
                          <span className="lesson-tags">
                            <span
                              className={`type-tag ${l.type === "Лекция" ? "lecture" : l.type === "Практика" ? "practice" : l.type === "Лабораторная" ? "lab" : "other"}`}
                            >
                              {l.type}
                            </span>
                            {l.uncertain && <span className="shared-tag">Сверьте недели</span>}
                            {l.moved && <span className="shared-tag">Перенос аудитории</span>}
                            {l.distance && <span className="shared-tag">Дистанционно</span>}
                          </span>
                          <strong>{l.subject}</strong>
                          <span className="lesson-meta">
                            <span>
                              <Icon name="groups" size={20} />
                              {l.groups.join(", ")}
                            </span>
                            {target.kind === "teacher" ? (
                              (l.rooms.length > 0 || l.place) && (
                                <span>
                                  <Icon name="location_on" size={20} />
                                  {[l.rooms.length > 0 && `Ауд. ${l.rooms.join(", ")}`, l.place].filter(Boolean).join(" · ")}
                                </span>
                              )
                            ) : (
                              l.teachers.length > 0 && (
                                <span>
                                  <Icon name="person" size={20} />
                                  {l.teachers.join(", ")}
                                </span>
                              )
                            )}
                          </span>
                          {l.condition && (
                            <span className="condition">
                              <Icon name="event_repeat" size={20} />
                              <span>Пометка в Word: {l.condition}</span>
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            day && (
              <div className="empty-state">
                <Icon name="event_busy" size={40} />
                <h3>Занятий нет</h3>
                <p>
                  {target.kind === "teacher"
                    ? "В этот день у преподавателя нет занятий по расписаниям."
                    : "В этот день аудитория не занята по расписаниям."}
                </p>
              </div>
            )
          )}
          {day && <CoverageNote coverage={day.coverage} />}
        </>
      )}

      {tab === "free" && (
        <>
          <DayPicker date={date} onChange={setDate} calendar={calendar} />
          <div className="pair-picker" aria-label="Пара">
            {(calendar?.bells || []).map((b) => (
              <button
                key={b.pair}
                className={b.pair === pair ? "selected" : ""}
                aria-pressed={b.pair === pair}
                aria-label={`${b.pair} пара, ${b.start}–${b.end}`}
                onClick={() => setPair(b.pair)}
              >
                <b>{b.pair}</b>
                <small>{b.start}</small>
              </button>
            ))}
          </div>
          {free && (
            <>
              <h3 className="search-date">
                {longDate(date)} · {free.bell.pair} пара, {free.bell.start}–{free.bell.end}
              </h3>
              <p className="free-summary">
                <strong>
                  Свободно {free.freeCount} {plural(free.freeCount, ["аудитория", "аудитории", "аудиторий"])}
                </strong>{" "}
                из {free.knownRooms} известных · занято {free.busyCount}
              </p>
              {free.free.map((g) => (
                <div className="result-group" key={g.letter || "plain"}>
                  <h3>{g.label || (g.letter ? `Номера с «${g.letter}»` : "Номера без буквы")}</h3>
                  <div className="room-chips">
                    {g.rooms.map((room) => (
                      <button
                        key={room}
                        onClick={() => {
                          setTab("people");
                          open({ kind: "room", room });
                        }}
                      >
                        {room}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {free.maybe.length > 0 && (
                <details
                  className="other-weeks"
                  open={openMaybe}
                  onToggle={(e) => setOpenMaybe((e.target as HTMLDetailsElement).open)}
                >
                  <summary>
                    <span>
                      Возможно заняты · {free.maybe.length}
                    </span>
                    <Icon name="keyboard_arrow_down" />
                  </summary>
                  <ul className="maybe-list">
                    {free.maybe.map((m) => (
                      <li key={m.room}>
                        <b>{m.room}</b>
                        {m.lessons.map((l, i) => (
                          <span key={i}>
                            {l.subject} · {l.groups.join(", ")} · пометка «{l.condition}» не распознана
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <CoverageNote coverage={free.coverage} free />
            </>
          )}
          {loading && !free && (
            <div className="schedule-skeleton" role="status" aria-label="Ищем свободные аудитории">
              <span />
            </div>
          )}
        </>
      )}
    </section>
  );
}
